import {Background} from "@nolimitcity/slot-game/bin/core/background/Background";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameMode, IGameModeChangedData} from "@nolimitcity/slot-game/bin/core/gamemode/GameMode";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {ResourcesGroupName} from "@nolimitcity/slot-game/bin/core/resource/ResourcesGroupName";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {SpinEvent} from "@nolimitcity/slot-game/bin/core/spin/event/SpinEvent";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {Linear, Power2, TimelineLite, TimelineMax, TweenLite} from "gsap";
import {Tesla} from "../game/Tesla";
import {TeslaGameAssets} from "../game/TeslaGameAssets";
import {TeslaGameConfig} from "../game/TeslaGameConfig";
import {TeslaSoundConfig} from "../game/TeslaSoundConfig";
import {MathHelper} from "../game/utils/MathHelper";

/**
 * Class description
 *
 * Created: 2017-06-20
 * @author jonas
 */
export class TeslaBackground extends Background {
    private _mainGameBackgroundLandscape:PIXI.Sprite;
    private _mainGameBackgroundOverlayLandscape:PIXI.Sprite;

    private _mainGameBackgroundPortrait:PIXI.Sprite;
    private _mainGameBackgroundOverlayPortrait:PIXI.Sprite;

    private _activeBackground:PIXI.Sprite;
    private _activeOverlay:PIXI.Sprite;

    private _overlayLayer:PIXI.Container;
    private _overlayContainer:PIXI.Container;
    private _roundAmbientLight:PIXI.Sprite;
    private _radialLightning:TimelineSprite;
    private _idleAnimations:TimelineMax;

    constructor() {
        super({layer : TeslaGameConfig.instance.LAYERS.BACKGROUND.name}, ResourcesGroupName.MAIN);
        this._overlayLayer = StageManager.getLayer(TeslaGameConfig.instance.LAYERS.BACKGROUND_OVERLAY.name);
    }

    protected initAnimations():void {
        this._mainGameBackgroundLandscape = new PIXI.Sprite(GameResources.getTextures(TeslaGameAssets.MAIN_GAME_LANDSCAPE)[0]);
        this._mainGameBackgroundLandscape.name = "Background Landscape";
        this._mainGameBackgroundLandscape.anchor.set(0.5, 0.5);

        this._mainGameBackgroundPortrait = new PIXI.Sprite(GameResources.getTextures(TeslaGameAssets.MAIN_GAME_PORTRAIT)[0]);
        this._mainGameBackgroundPortrait.name = "Background Portrait";
        this._mainGameBackgroundPortrait.anchor.set(0.5, 0.5);

        this._mainGameBackgroundOverlayLandscape = new PIXI.Sprite(GameResources.getTextures(TeslaGameAssets.MAIN_GAME_OVERLAY_LANDSCAPE)[0]);
        this._mainGameBackgroundOverlayLandscape.anchor.set(0.5, 0.5);
        this._mainGameBackgroundOverlayPortrait = new PIXI.Sprite(GameResources.getTextures(TeslaGameAssets.MAIN_GAME_OVERLAY_PORTRAIT)[0]);
        this._mainGameBackgroundOverlayPortrait.anchor.set(0.5, 0.5);

        this._roundAmbientLight = new PIXI.Sprite(GameResources.getTextures(TeslaGameAssets.ROUND_AMBIENT_LIGHT)[0]);
        this._roundAmbientLight.anchor.set(0.5, 0.5);

        this._radialLightning = new TimelineSprite(GameResources.getTextures(TeslaGameAssets.RADIAL_EFFECT));
        this._radialLightning.anchor.set(0.5, 0.5);

        this._mainGameBackgroundOverlayLandscape.alpha = 0;
        this._mainGameBackgroundOverlayPortrait.alpha = 0;
        this._roundAmbientLight.alpha = 0;
        this._radialLightning.hide();

        this._mainGameBackgroundOverlayLandscape.blendMode = PIXI.BLEND_MODES.ADD;
        this._mainGameBackgroundOverlayPortrait.blendMode = PIXI.BLEND_MODES.ADD;
        this._roundAmbientLight.blendMode = PIXI.BLEND_MODES.ADD;
        this._radialLightning.blendMode = PIXI.BLEND_MODES.ADD;

        this._layer.addChild(this);
        this._overlayContainer = new PIXI.Container();

        this._overlayLayer.addChild(this._overlayContainer);

        EventHandler.addEventListener(this, SpinEvent.BEFORE_START, () => this.endIdleAnimations());
        EventHandler.addEventListener(this, SpinEvent.IDLE, () => this.createIdleAnimations());

    }

    protected onChangeBackground(data:IGameModeChangedData):boolean {
        const changed = super.onChangeBackground(data);
        if(changed) {
            if(data.newMode == GameMode.NORMAL) {
                if(this._mainGameBackgroundLandscape) {
                    this._mainGameBackgroundLandscape.visible = true;
                }
            }
        }
        return changed;
    }


    public getWinAnimation(duration:number, winRatio?:number):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        tl.add(this.getIndividualBlinkAnimation());
        if(winRatio && winRatio > 1) {
            tl.add(Tesla.backgroundWindowAnimation.getWindowLightning());
        }

        const count = Math.floor((duration * 10) / 2);

        let lastBlink:TimelineLite = this.getIndividualBlinkAnimation();
        tl.add(lastBlink, duration - lastBlink.totalDuration());

        for(let i:number = 0; i < count; i++) {
            tl.add(this.getIndividualBlinkAnimation(), MathHelper.randomNumberFromInterval(0, duration - lastBlink.totalDuration()));
        }
        return tl;
    }

    public getBlinkAnimation():TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        tl.add([
                this._radialLightning.getAnimationAutoShowHide(true, true, [0, 21, 0]),
                Tesla.backgroundWindowAnimation.getWindowLightning()
            ]
        );
        tl.add(this.getIndividualBlinkAnimation(), 0);
        tl.add(this.getIndividualBlinkAnimation(), 3 / 30);
        tl.add(this.getIndividualBlinkAnimation(), 5 / 30);
        tl.add(this.getIndividualBlinkAnimation(), 8 / 30);
        tl.add(this.getIndividualBlinkAnimation(), 10 / 30);
        tl.add(this.getIndividualBlinkAnimation(), 16 / 30);
        tl.add(this.getIndividualBlinkAnimation(), 21 / 30);

        tl.add(this.getIndividualBlinkAnimation(), 24 / 30);
        tl.add(this.getIndividualBlinkAnimation(), 26 / 30);
        tl.add(this.getIndividualBlinkAnimation(), 30 / 30);
        tl.add(this.getIndividualBlinkAnimation(), 34 / 30);
        tl.add(this.getIndividualBlinkAnimation(), 36 / 30);
        tl.add(this.getIndividualBlinkAnimation(), 39 / 30);
        tl.add(this.getIndividualBlinkAnimation(), 41 / 30);

        return tl;
    }

    public getIndividualBlinkAnimation():TimelineLite {
        const targetAlpha:number = MathHelper.randomNumberFromInterval(0.5, 1);
        const blinkAnimation:TimelineLite = new TimelineLite();
        blinkAnimation.add([
            new TweenLite(this._activeOverlay, 0.03, {alpha : targetAlpha, ease : Linear.easeNone}),
            new TweenLite(this._roundAmbientLight, 0.03, {alpha : targetAlpha, ease : Linear.easeNone})
        ]);
        blinkAnimation.add([
            new TweenLite(this._activeOverlay, 0.3, {alpha : 0, ease : Power2.easeOut}),
            new TweenLite(this._roundAmbientLight, 0.3, {alpha : 0, ease : Power2.easeOut})
        ]);
        return blinkAnimation;
    }

    protected onResize(resizeData:IResizeData) {
        this._overlayContainer.removeChild(this._activeOverlay);
        if(resizeData.orientation == Orientation.PORTRAIT) {
            this._activeBackground = this._mainGameBackgroundPortrait;
            this._activeOverlay = this._mainGameBackgroundOverlayPortrait;
            this._roundAmbientLight.position.set(936, -95);
            this._roundAmbientLight.scale.set(1.6, 1.6);
            this._radialLightning.scale.set(1.65, 1.65);
            this._radialLightning.position.set(936, -95);
        } else {
            this._activeBackground = this._mainGameBackgroundLandscape;
            this._activeOverlay = this._mainGameBackgroundOverlayLandscape;
            this._roundAmbientLight.position.set(1180, 105);
            this._roundAmbientLight.scale.set(1.1, 1.1);
            this._radialLightning.scale.set(1.15, 1.15);
            this._radialLightning.position.set(1180, 105);
        }
        this._activeBackground.position.set(640, 360);
        this._activeOverlay.position.set(640, 360);


        this.removeChildren();
        this.addChild(this._activeBackground);

        this._overlayContainer.addChild(this._activeOverlay);
        this._overlayContainer.addChild(this._roundAmbientLight);
        this._overlayContainer.addChild(this._radialLightning);
    }

    private createIdleAnimations() {
        this._idleAnimations = new TimelineMax({repeat : -1, repeatDelay : 15, delay : 15});
        this._idleAnimations.add([
            this.getBlinkAnimation(),
            () => SlotGame.sound.playEffect(TeslaSoundConfig.instance.FS_INTRO_COIL_LIGHTNING)
        ]);
    }

    private endIdleAnimations() {
        this._activeOverlay.alpha = 0;
        this._roundAmbientLight.alpha = 0;
        Tesla.backgroundWindowAnimation.resetView();
        this._radialLightning.hide();
        if(this._idleAnimations) {
            this._idleAnimations.kill();
        }

    }
}